import type { AdminOrganizationRow } from '../../../domain/superadmin/types';
import { formatCurrency, formatDate } from '../../../utils/formatters';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../../components/ui/table';
import { OrganizationStatusBadge } from './OrganizationStatusBadge';
import {
    ImpersonateButton,
    OrgIdentity,
    OrganizationCard,
    PlanCell,
    WhatsAppCell,
} from './OrganizationCard';

export interface OrganizationsTableProps {
    items: AdminOrganizationRow[];
    onOpen: (orgId: string) => void;
    onImpersonate?: (org: AdminOrganizationRow) => void;
}

const HEAD =
    'bg-card text-[11px] font-semibold uppercase tracking-[0.07em] text-muted';
const COLUMNS = [
    'Empresa',
    'Plano',
    'MRR',
    'Status',
    'Equipe',
    'WhatsApp',
    'Cadastro',
] as const;

export function OrganizationsTable({ items, onOpen, onImpersonate }: OrganizationsTableProps) {
    if (items.length === 0) {
        return (
            <p className="rounded-xl border border-dashed border-border bg-surface px-s5 py-s8 text-center text-[13px] text-muted">
                Nenhuma empresa encontrada.
            </p>
        );
    }

    return (
        <>
            <div className="flex flex-col gap-s3 md:hidden">
                {items.map((row) => (
                    <OrganizationCard
                        key={row.id}
                        row={row}
                        onOpen={onOpen}
                        onImpersonate={onImpersonate}
                    />
                ))}
            </div>

            <div className="hidden overflow-hidden rounded-xl border border-subtle bg-card shadow-sm md:block">
                <Table>
                    <TableHeader>
                        <TableRow className="border-b border-subtle hover:bg-transparent">
                            {COLUMNS.map((label) => (
                                <TableHead key={label} className={HEAD}>
                                    {label}
                                </TableHead>
                            ))}
                            {onImpersonate && (
                                <TableHead className={HEAD}>
                                    <span className="sr-only">Ações</span>
                                </TableHead>
                            )}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {items.map((row) => (
                            <TableRow
                                key={row.id}
                                className="cursor-pointer border-b border-subtle last:border-b-0 transition-colors hover:bg-surface-subtle"
                                onClick={() => onOpen(row.id)}
                            >
                                <TableCell>
                                    <OrgIdentity row={row} />
                                </TableCell>
                                <TableCell>
                                    <PlanCell row={row} />
                                </TableCell>
                                <TableCell>
                                    <span className="font-mono text-[13px] tabular-nums text-default">
                                        {formatCurrency(row.mrrCents / 100)}
                                    </span>
                                </TableCell>
                                <TableCell>
                                    <OrganizationStatusBadge row={row} />
                                </TableCell>
                                <TableCell>
                                    <span className="font-mono text-[13px] font-medium tabular-nums text-default">
                                        {row.memberCount}
                                    </span>
                                </TableCell>
                                <TableCell>
                                    <WhatsAppCell
                                        connected={row.whatsappConnected}
                                        max={row.whatsappMax}
                                    />
                                </TableCell>
                                <TableCell>
                                    <span className="font-mono text-[12.5px] tabular-nums text-muted">
                                        {formatDate(row.createdAt)}
                                    </span>
                                </TableCell>
                                {onImpersonate && (
                                    <TableCell onClick={(e) => e.stopPropagation()}>
                                        <ImpersonateButton
                                            row={row}
                                            onImpersonate={onImpersonate}
                                        />
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
        </>
    );
}
